import { Component, OnInit, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { takeUntil } from 'rxjs';
import { BaseComponent } from '../../../core/components/base/base.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import { GroupService } from '../../../core/services/group.service';
import { SurveyTemplateService } from '../../../core/services/survey-template.service';
import { AppointmentTypeService } from '../../../core/services/appointment-type.service';
import { AppointmentTypeSurveyTemplateService } from '../../../core/services/appointment-type-survey-template.service';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

@Component({
    selector: 'app-survey-template-settings',
    templateUrl: './survey-template-settings.component.html',
    styleUrls: ['./survey-template-settings.component.scss']
})
export class SurveyTemplateSettingsComponent extends BaseComponent implements OnInit {
    @ViewChildren(MatExpansionPanel) expansionPanels!: QueryList<MatExpansionPanel>;
    @ViewChildren('sectionTitleInput') sectionTitleInputs!: QueryList<ElementRef>;

    templatesForm!: FormGroup;
    isLoading = false;
    isSaving = false;
    isLinking = false;
    selectedGroup: any;
    
    expandedTemplates: Map<string, boolean> = new Map();

    appointmentTypes: any[] = [];
    linkedAppointmentTypes: Map<number, any[]> = new Map();

    selectedAppointmentTypeId: { [templateIndex: number]: string | null } = {};

    constructor(
        private formBuilder: FormBuilder,
        private surveyTemplateService: SurveyTemplateService,
        private groupService: GroupService,
        private appointmentTypeService: AppointmentTypeService,
        private appointmentTypeSurveyTemplateService: AppointmentTypeSurveyTemplateService,
        fs: FeedbackService,
        md: MatDialog
    ) {
        super(fs, md);
    }

    ngOnInit(): void {
        this.templatesForm = this.formBuilder.group({
            templates: this.formBuilder.array([])
        });
        this.loadGroupAndTemplates();
    }

    get templatesArray(): FormArray {
        return this.templatesForm.get('templates') as FormArray;
    }

    private loadGroupAndTemplates(): void {
        this.isLoading = true;
        this.groupService.selectedGroup$
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (group: any) => {
                    this.selectedGroup = group;
                    if (group) {
                        this.loadSurveyTemplates(group.id);
                        this.loadAppointmentTypes();
                    }
                },
                error: (error: any) => {
                    console.error('Error loading group:', error);
                    this.isLoading = false;
                }
            });
    }

    private loadSurveyTemplates(groupId: string): void {
        this.surveyTemplateService.getAllSurveyTemplates(groupId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response.data && response.data.length > 0) {
                        this.templatesArray.clear();
                        response.data.forEach((templateData: any) => {
                            const templateFg = this.createTemplateFormGroup(templateData);

                            const initialPreset = this.getInitialTimingPreset(templateData);
                            templateFg.get('timingPreset')?.setValue(initialPreset, { emitEvent: false });
                            this.updateTimingFromPreset(templateFg, initialPreset);

                            // Set initial disabled state for timing controls
                            if (!templateFg.get('surveyEnabled')?.value) {
                                templateFg.get('timingPreset')?.disable({ emitEvent: false });
                                templateFg.get('surveyDelayDays')?.disable({ emitEvent: false });
                                templateFg.get('surveyDelayHours')?.disable({ emitEvent: false });
                            }

                            // Set up valueChanges subscriptions
                            templateFg.get('timingPreset')?.valueChanges.pipe(
                                takeUntil(this.destroy$)
                            ).subscribe(preset => {
                                if (preset) {
                                    this.updateTimingFromPreset(templateFg, preset);
                                }
                            });

                            templateFg.get('surveyEnabled')?.valueChanges.pipe(
                                takeUntil(this.destroy$)
                            ).subscribe(isEnabled => {
                                const timingPresetControl = templateFg.get('timingPreset');
                                const delayDaysControl = templateFg.get('surveyDelayDays');
                                const delayHoursControl = templateFg.get('surveyDelayHours');

                                if (isEnabled) {
                                    timingPresetControl?.enable();
                                    delayDaysControl?.enable();
                                    delayHoursControl?.enable();
                                    if (!timingPresetControl?.value) {
                                        timingPresetControl?.setValue('1day');
                                    }
                                } else {
                                    timingPresetControl?.disable();
                                    delayDaysControl?.disable();
                                    delayHoursControl?.disable();
                                }
                            });

                            this.templatesArray.push(templateFg);
                        });
                        this.loadLinkedAppointmentTypes();
                    }
                    this.isLoading = false;
                },
                error: (error) => {
                    console.error('Error loading survey templates:', error);
                    this.isLoading = false;
                    this.showToastMessage('Error loading survey templates');
                }
            });
    }

    private getInitialTimingPreset(template: any): string {
        const days = template.surveyDelayDays ?? 1;
        const hours = template.surveyDelayHours ?? 0;

        if (!template.surveyEnabled) return '';

        if (days === 0 && hours === 0) return 'immediate';
        if (days === 0 && hours === 1) return '1hour';
        if (days === 0 && hours === 6) return '6hours';
        if (days === 1 && hours === 0) return '1day';
        if (days === 3 && hours === 0) return '3days';
        if (days === 7 && hours === 0) return '1week';
        return 'custom';
    }

    private createTemplateFormGroup(template: any): FormGroup {
        return this.formBuilder.group({
            id: [template.id],
            name: [template.name || ''],
            description: [template.description || ''],
            surveyTitle: [template.surveyTitle || '', Validators.required],
            surveyIntroduction: [template.surveyIntroduction || ''],
            surveyEnabled: [template.surveyEnabled || false],
            surveyDelayHours: [template.surveyDelayHours ?? 0, [Validators.min(0), Validators.max(23)]],
            surveyDelayDays: [template.surveyDelayDays ?? 1, [Validators.min(0), Validators.max(30)]],
            timingPreset: [''],
            surveyConfig: this.formBuilder.group({
                sections: this.formBuilder.array(
                    template.surveyConfig?.sections?.map((section: any) => this.createSectionFormGroup(section)) || []
                )
            })
        });
    }

    private createSectionFormGroup(section: any): FormGroup {
        return this.formBuilder.group({
            id: [section.id || this.generateId()],
            title: [section.title || '', Validators.required],
            description: [section.description || ''],
            questions: this.formBuilder.array(
                section.questions ? section.questions.map((question: any) => this.createQuestionFormGroup(question)) : []
            )
        });
    }

    private createQuestionFormGroup(question: any): FormGroup {
        const controls: any = {
            id: [question.id || this.generateId()],
            type: [question.type || 'radio', Validators.required],
            question: [question.question || '', Validators.required],
            required: [question.required || false],
            placeholder: [question.placeholder || '']
        };

        if (question.type === 'radio') {
            controls.options = this.formBuilder.array(
                question.options ? question.options.map((option: any) => this.createOptionFormGroup(option)) : []
            );
        }

        return this.formBuilder.group(controls);
    }

    private createOptionFormGroup(option: any): FormGroup {
        return this.formBuilder.group({
            value: [option.value || '', Validators.required],
            text: [option.text || '', Validators.required],
            score: [option.score || 1, [Validators.required, Validators.min(1), Validators.max(5)]]
        });
    }

    private generateId(): string {
        return 'id_' + Math.random().toString(36).substr(2, 9);
    }

    getSectionsArray(templateIndex: number): FormArray {
        return this.templatesArray.at(templateIndex).get('surveyConfig.sections') as FormArray;
    }

    getOptionsArray(templateIndex: number, sectionIndex: number, questionIndex: number): FormArray {
        return this.getSectionsArray(templateIndex).at(sectionIndex).get('questions.' + questionIndex + '.options') as FormArray;
    }

    getSurveyEnabledControl(templateIndex: number): FormControl {
        return this.templatesArray.at(templateIndex).get('surveyEnabled') as FormControl;
    }

    addSection(templateIndex: number): void {
        const sectionsArray = this.getSectionsArray(templateIndex);
        const newSection = this.createSectionFormGroup({
            id: this.generateId(),
            title: '',
            description: '',
            questions: []
        });
        sectionsArray.push(newSection);
    }

    removeSection(templateIndex: number, sectionIndex: number): void {
        const sectionsArray = this.getSectionsArray(templateIndex);
        sectionsArray.removeAt(sectionIndex);
    }

    onSectionDrop(event: CdkDragDrop<any>, templateIndex: number) {
        const sectionsArray = this.getSectionsArray(templateIndex);
        const sectionToMove = sectionsArray.at(event.previousIndex);
        sectionsArray.removeAt(event.previousIndex);
        sectionsArray.insert(event.currentIndex, sectionToMove);
    }

    isSectionExpanded(templateId: string, sectionId: string): boolean {
        return this.expandedTemplates.get(templateId + '-' + sectionId) ?? false;
    }

    toggleSection(templateId: string, sectionId: string): void {
        const key = templateId + '-' + sectionId;
        this.expandedTemplates.set(key, !(this.expandedTemplates.get(key) ?? false));
    }

    asFormArray(control: AbstractControl | null): FormArray {
        return control as FormArray;
    }

    addQuestion(templateIndex: number, sectionIndex: number): void {
        const questionsArray = this.getSectionsArray(templateIndex).at(sectionIndex).get('questions') as FormArray;
        const newQuestion = this.createQuestionFormGroup({
            id: this.generateId(),
            type: 'radio',
            question: '',
            required: false,
            options: []
        });
        questionsArray.push(newQuestion);
    }

    removeQuestion(templateIndex: number, sectionIndex: number, questionIndex: number): void {
        const questionsArray = this.getSectionsArray(templateIndex).at(sectionIndex).get('questions') as FormArray;
        questionsArray.removeAt(questionIndex);
    }

    addOption(templateIndex: number, sectionIndex: number, questionIndex: number): void {
        const optionsArray = this.getOptionsArray(templateIndex, sectionIndex, questionIndex);
        const newOption = this.createOptionFormGroup({
            value: '',
            text: '',
            score: 1
        });
        optionsArray.push(newOption);
    }

    removeOption(templateIndex: number, sectionIndex: number, questionIndex: number, optionIndex: number): void {
        const optionsArray = this.getOptionsArray(templateIndex, sectionIndex, questionIndex);
        optionsArray.removeAt(optionIndex);
    }

    onQuestionTypeChange(templateIndex: number, sectionIndex: number, questionIndex: number, type: string): void {
        const questionGroup = this.getSectionsArray(templateIndex).at(sectionIndex).get('questions')?.get([questionIndex]) as FormGroup;
        if (type === 'radio') {
            if (!questionGroup.get('options')) {
                const optionsArray = this.formBuilder.array([]);
                (questionGroup as any).addControl('options', optionsArray);
            }
        } else {
            (questionGroup as any).removeControl('options');
        }
    }

    private updateTimingFromPreset(templateFg: FormGroup, preset: string): void {
        switch (preset) {
            case 'immediate':
                templateFg.patchValue({ surveyDelayDays: 0, surveyDelayHours: 0 }, { emitEvent: false });
                break;
            case '1hour':
                templateFg.patchValue({ surveyDelayDays: 0, surveyDelayHours: 1 }, { emitEvent: false });
                break;
            case '6hours':
                templateFg.patchValue({ surveyDelayDays: 0, surveyDelayHours: 6 }, { emitEvent: false });
                break;
            case '1day':
                templateFg.patchValue({ surveyDelayDays: 1, surveyDelayHours: 0 }, { emitEvent: false });
                break;
            case '3days':
                templateFg.patchValue({ surveyDelayDays: 3, surveyDelayHours: 0 }, { emitEvent: false });
                break;
            case '1week':
                templateFg.patchValue({ surveyDelayDays: 7, surveyDelayHours: 0 }, { emitEvent: false });
                break;
            case 'custom':
                break;
        }
    }

    saveTemplate(templateIndex: number): void {
        const templateFormGroup = this.templatesArray.at(templateIndex);
        if (templateFormGroup.invalid) {
            this.showToastMessage('Please fill in all required fields for this template');
            return;
        }

        this.isSaving = true;
        const formValue = templateFormGroup.value;

        this.surveyTemplateService.updateSurveyTemplate(formValue.id, formValue)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.showToastMessage('Survey template saved successfully');
                    this.isSaving = false;
                },
                error: (error) => {
                    console.error('Error saving survey template:', error);
                    this.showToastMessage('Error saving survey template');
                    this.isSaving = false;
                }
            });
    }

    async resetTemplate(templateIndex: number): Promise<void> {
        if (await this.showConfirmationDialog('Reset Template', 'Are you sure you want to reset this survey template to default? This action cannot be undone.')) {
            this.isSaving = true;
            const templateId = this.templatesArray.at(templateIndex).get('id')?.value;
            this.surveyTemplateService.resetSurveyTemplate(templateId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (response) => {
                        this.templatesArray.setControl(templateIndex, this.createTemplateFormGroup(response.data));
                        this.showToastMessage('Survey template reset to default');
                        this.isSaving = false;
                    },
                    error: (error) => {
                        console.error('Error resetting survey template:', error);
                        this.showToastMessage('Error resetting survey template');
                        this.isSaving = false;
                    }
                });
        }
    }

    previewTemplate(): void {
        const formValue = this.templatesForm.value;
        //console.log('Preview template:', formValue);
        this.showToastMessage('Preview functionality coming soon');
    }

    private loadAppointmentTypes(): void {
        if (!this.selectedGroup) return;
        
        this.appointmentTypeService.getAppointmentTypesByGroup(this.selectedGroup.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.appointmentTypes = response || [];
                },
                error: (error) => {
                    console.error('Error loading appointment types:', error);
                }
            });
    }

    private loadLinkedAppointmentTypes(): void {
        this.templatesArray.controls.forEach((templateControl, index) => {
            const templateId = templateControl.get('id')?.value;
            if (templateId) {
                this.appointmentTypeSurveyTemplateService.getAppointmentTypesForSurveyTemplate(templateId)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (response) => {
                            this.linkedAppointmentTypes.set(index, response.data || []);
                        },
                        error: (error) => {
                            console.error('Error loading linked appointment types:', error);
                            this.linkedAppointmentTypes.set(index, []);
                        }
                    });
            }
        });
    }

    getLinkedAppointmentTypes(templateIndex: number): any[] {
        return this.linkedAppointmentTypes.get(templateIndex) || [];
    }

    getAvailableAppointmentTypes(templateIndex: number): any[] {
        const linkedTypes = this.getLinkedAppointmentTypes(templateIndex);
        const linkedTypeIds = linkedTypes.map(lt => lt.id);
        return this.appointmentTypes.filter(at => !linkedTypeIds.includes(at.id));
    }

    onAppointmentTypeSelectionChange(templateIndex: number, appointmentTypeId: string): void {
        if (!appointmentTypeId) return;
        this.selectedAppointmentTypeId[templateIndex] = appointmentTypeId;
        this.linkAppointmentType(templateIndex, appointmentTypeId);
    }

    linkAppointmentType(templateIndex: number, appointmentTypeId: string): void {
        const templateId = this.templatesArray.at(templateIndex).get('id')?.value;
        if (!templateId || !appointmentTypeId) return;
        this.appointmentTypeSurveyTemplateService.linkSurveyTemplateToAppointmentType(
            appointmentTypeId,
            templateId,
            false
        ).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.loadLinkedAppointmentTypes();
                this.selectedAppointmentTypeId[templateIndex] = null;
                this.showToastMessage('Appointment type linked successfully');
            },
            error: (error) => {
                console.error('Error linking appointment type:', error);
                this.showToastMessage('Error linking appointment type');
            }
        });
    }

    unlinkAppointmentType(templateIndex: number, appointmentTypeId: string): void {
        const templateId = this.templatesArray.at(templateIndex).get('id')?.value;
        if (!templateId || !appointmentTypeId) return;
        this.appointmentTypeSurveyTemplateService.unlinkSurveyTemplateFromAppointmentType(
            appointmentTypeId,
            templateId
        ).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.loadLinkedAppointmentTypes();
                this.showToastMessage('Appointment type unlinked successfully');
            },
            error: (error) => {
                console.error('Error unlinking appointment type:', error);
                this.showToastMessage('Error unlinking appointment type');
            }
        });
    }

    getFormValidationErrors(form: AbstractControl): { controlName: string, errorName: string, errorValue: any }[] {
        const errors: { controlName: string, errorName: string, errorValue: any }[] = [];
        if (!form) {
            return errors;
        }

        if (form instanceof FormGroup || form instanceof FormArray) {
            Object.keys(form.controls).forEach(key => {
                const control = form.get(key);
                if (control) {
                    const controlErrors = control.errors;
                    if (controlErrors) {
                        Object.keys(controlErrors).forEach(keyError => {
                            errors.push({
                                controlName: key,
                                errorName: keyError,
                                errorValue: controlErrors[keyError]
                            });
                        });
                    }

                    if (control instanceof FormGroup || control instanceof FormArray) {
                        errors.push(...this.getFormValidationErrors(control));
                    }
                }
            });
        }

        return errors;
    }
} 